import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import VehicleCheckItemsSettings from "@/components/settings/VehicleCheckItemsSettings";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { useAppContext } from "@/contexts/AppContext";
import { canViewTab, canEditTab } from "@/lib/permissions";
import { supabase } from "@/lib/supabase";
import { useTenantModules } from "@/hooks/useTenantModules";

import PaymentSummary from "./PaymentSummary";
import DriverManagement from "./DriverManagement";
import FleetManagement from "./FleetManagement";
import BonusTierManagement from "./BonusTierManagement";
import TimesheetReport from "./TimesheetReport";
import ReportUpload from "./ReportUpload";
import AnnualSummaryExport from "./AnnualSummaryExport";
import PaymentTypeManagement from "./PaymentTypeManagement";
import EmailReportForm from "./EmailReportForm";
import ClosedCheck from "./ClosedCheck";
import MatchHub from "./MatchHub";
import PayoutHub from "./PayoutHub";
import WeeklyPayoutWorkspace from "./WeeklyPayoutWorkspace";
import JobInput from "./JobInput";
import AdminECardHub from "@/pages/AdminECardHub";
import CRMAccountsPage from "./CRMAccountsPage";
import ExportBuilder from "./ExportBuilder";
import RosterPage from "@/pages/RosterPage";
import FleetCapacityViewPage from "@/pages/FleetCapacityViewPage";
import RosterPositionsManagement from "./RosterPositionsManagement";
import VehicleTypeCapacitySettings from "./settings/VehicleTypeCapacitySettings";
import TeamAccessSettings from "./settings/TeamAccessSettings";
import DriverNoticeBoardSettings from "./settings/DriverNoticeBoardSettings";
import TaxiCallerCompaniesSettings from "./settings/TaxiCallerCompaniesSettings";
import { loadVehicleTypes, type VehicleTypeRow } from "@/lib/vehicleTypes";
import RosterGroupsManagement from "./RosterGroupsManagement";
import RosterHolidayManagement from "./RosterHolidayManagement";
import FleetPulse from "./FleetPulse";
import DeductionCategoriesManager from "./DeductionCategoriesManager";
import PaymentCategoriesManager from "./PaymentCategoriesManager";
import PayoutColumnMappingManager from "./PayoutColumnMappingManager";
import DriverPaymentBuilder from "./settings/DriverPaymentBuilder";
import MaintenanceSettings from "./maintenance/MaintenanceSettings";
import IvrGeneralSettings from "./settings/IvrGeneralSettings";
import CompanySettings from "./settings/CompanySettings";
import CompanyBrandingSettings from "./settings/CompanyBrandingSettings";
import WorkTimeRulesSettings from "./WorkTimeRulesSettings";
import InsuranceHub from "./InsuranceHub";
import InsuranceBrokerSettings from "./InsuranceBrokerSettings";
import InsuranceExcessSettings from "./InsuranceExcessSettings";
import IncidentReportFieldSettings from "./settings/IncidentReportFieldSettings";
import IvrCallsHub from "./IvrCallsHub";
import CommsBook from "./CommsBook";
import SettingsSidebarLayout from "./ui/settings-sidebar-layout";
import FolderTabLayout from "./ui/folder-tab-layout";
import {
  LogOut,
  Mail,
  Image,
  Upload,
  Save,
  RefreshCw,
  CheckCircle2,
  GitMerge,
  BarChart3,
  Clock3,
  Users,
  Car,
  Activity,
  CalendarDays,
  ClipboardPlus,
  FileText,
  Building2,
  FileSpreadsheet,
  CreditCard,
  WalletCards,
  Settings,
  Palette,
  CreditCardIcon,
  SlidersHorizontal,
  BadgeDollarSign,
  Gift,
  ShieldCheck,
  BriefcaseBusiness,
  UsersRound,
  CalendarClock,
  Gauge,
  Wrench,
  Fuel,
  Plus,
  Trash2,
  X,
  UserCog,
  LayoutTemplate,
  Edit,
  Phone,
  PhoneCall,
  Timer,
  Map as MapIcon,
  ShieldAlert,
  Landmark,
  FileWarning,
  MessageSquare,
  Shield,
  Megaphone,
  Webhook,

} from "lucide-react";
// Admin Issues popup disabled. Alerts now handled inside Closed Check → Action Required tabs.
// import AdminIssuesDialog from "./AdminIssuesDialog";
import { ADMIN_NAV_EVENT, AdminNavDetail } from "@/lib/adminNav";
import {
  monthsToCsv,
  parseMonthsCsv,
  monthLabel,
  DEFAULT_REGO_ALLOWED_MONTHS,
  DEFAULT_COF_ALLOWED_MONTHS,
  DEFAULT_COF_INTERVAL_MONTHS,
  DEFAULT_COF_FLEX_MONTHS,
} from "@/lib/fleetSpacing";
import AdminSiteMap from "./settings/AdminSiteMap";

type TenantRow = {
  id: string;
  tenant_name: string;
  company_login_code: string | null;
};

type DriverTypeRow = {
  id: string;
  tenant_id: string;
  type_key: string;
  type_name: string;
  description: string | null;
  is_waged: boolean;
  is_commission: boolean;
  is_contractor: boolean;
  is_leased: boolean;
  default_commission_percent: number | null;
  uses_bonus_tiers: boolean;
  uses_payment_type_percentages: boolean;
  allows_deductions: boolean;
  is_active: boolean;
  created_at?: string | null;
};

type DriverTypeForm = {
  id?: string;
  tenant_id: string;
  type_key: string;
  type_name: string;
  description: string;
  is_waged: boolean;
  is_commission: boolean;
  is_contractor: boolean;
  is_leased: boolean;
  default_commission_percent: string;
  uses_bonus_tiers: boolean;
  uses_payment_type_percentages: boolean;
  allows_deductions: boolean;
  is_active: boolean;
};

const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

const emptyForm = (tenantId: string): DriverTypeForm => ({
  tenant_id: tenantId,
  type_key: "",
  type_name: "",
  description: "",
  is_waged: false,
  is_commission: true,
  is_contractor: false,
  is_leased: false,
  default_commission_percent: "50",
  uses_bonus_tiers: true,
  uses_payment_type_percentages: true,
  allows_deductions: true,
  is_active: true,
});

const makeTypeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const DriverTypeManagement: React.FC = () => {
  const [tenant, setTenant] = useState<TenantRow | null>(null);
  const [driverTypes, setDriverTypes] = useState<DriverTypeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<DriverTypeForm>(emptyForm(DEFAULT_TENANT_ID));

  const tenantId = tenant?.id || DEFAULT_TENANT_ID;

  const sortedDriverTypes = useMemo(
    () =>
      [...driverTypes].sort((a, b) =>
        a.type_name.localeCompare(b.type_name, undefined, { sensitivity: "base" }),
      ),
    [driverTypes],
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    const { data: tenantData, error: tenantError } = await supabase
      .from("tenants")
      .select("id, tenant_name, company_login_code")
      .eq("id", DEFAULT_TENANT_ID)
      .maybeSingle();

    if (tenantError) {
      setLoading(false);
      toast({
        title: "Could not load company",
        description: tenantError.message,
        variant: "destructive",
      });
      return;
    }

    const activeTenant: TenantRow =
      tenantData || {
        id: DEFAULT_TENANT_ID,
        tenant_name: "Taxis Gold & Black",
        company_login_code: "TG&B",
      };

    setTenant(activeTenant);

    const { data, error } = await supabase
      .from("driver_types")
      .select("*")
      .eq("tenant_id", activeTenant.id)
      .order("type_name", { ascending: true });

    setLoading(false);

    if (error) {
      toast({
        title: "Could not load driver types",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setDriverTypes((data || []) as DriverTypeRow[]);
    setForm(emptyForm(activeTenant.id));
  };

  const startAdd = () => {
    setForm(emptyForm(tenantId));
    setShowForm(true);
  };

  const startEdit = (row: DriverTypeRow) => {
    setForm({
      id: row.id,
      tenant_id: row.tenant_id,
      type_key: row.type_key || "",
      type_name: row.type_name || "",
      description: row.description || "",
      is_waged: !!row.is_waged,
      is_commission: !!row.is_commission,
      is_contractor: !!row.is_contractor,
      is_leased: !!row.is_leased,
      default_commission_percent:
        row.default_commission_percent === null || row.default_commission_percent === undefined
          ? ""
          : String(row.default_commission_percent),
      uses_bonus_tiers: !!row.uses_bonus_tiers,
      uses_payment_type_percentages: !!row.uses_payment_type_percentages,
      allows_deductions: !!row.allows_deductions,
      is_active: !!row.is_active,
    });
    setShowForm(true);
  };

  const updateForm = <K extends keyof DriverTypeForm>(field: K, value: DriverTypeForm[K]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "type_name" && !prev.id) {
        next.type_key = makeTypeKey(String(value));
      }

      return next;
    });
  };

  const validateForm = () => {
    if (!form.type_name.trim()) {
      toast({
        title: "Driver type needs a name",
        description: "Example: Commission, Contractor, Leased, Owner Operator.",
        variant: "destructive",
      });
      return false;
    }

    const key = makeTypeKey(form.type_key || form.type_name);

    if (!key) {
      toast({
        title: "Driver type needs a key",
        description: "The key is used internally and should be simple text.",
        variant: "destructive",
      });
      return false;
    }

    if (form.default_commission_percent !== "") {
      const commission = Number(form.default_commission_percent);
      if (Number.isNaN(commission) || commission < 0 || commission > 100) {
        toast({
          title: "Commission must be between 0 and 100",
          description: "Leave it blank for driver types that do not use commission.",
          variant: "destructive",
        });
        return false;
      }
    }

    return true;
  };

  const saveDriverType = async () => {
    if (!validateForm()) return;

    setSaving(true);

    const payload = {
      tenant_id: tenantId,
      type_key: makeTypeKey(form.type_key || form.type_name),
      type_name: form.type_name.trim(),
      description: form.description.trim() || null,
      is_waged: form.is_waged,
      is_commission: form.is_commission,
      is_contractor: form.is_contractor,
      is_leased: form.is_leased,
      default_commission_percent:
        form.default_commission_percent === "" ? null : Number(form.default_commission_percent),
      uses_bonus_tiers: form.uses_bonus_tiers,
      uses_payment_type_percentages: form.uses_payment_type_percentages,
      allows_deductions: form.allows_deductions,
      is_active: form.is_active,
    };

    const query = form.id
      ? supabase.from("driver_types").update(payload).eq("id", form.id).select()
      : supabase.from("driver_types").insert([payload]).select();

    const { data, error } = await query;

    setSaving(false);

    if (error) {
      toast({
        title: "Driver type not saved",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    if (!data || data.length === 0) {
      toast({
        title: "Nothing was saved",
        description:
          "The change did not reach the database (no matching record was updated). Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Driver type saved",
      description: `${payload.type_name} has been saved.`,
    });

    setShowForm(false);
    setForm(emptyForm(tenantId));
    await loadData();
  };


  const deleteDriverType = async (row: DriverTypeRow) => {
    const confirmDelete = window.confirm(
      `Delete driver type "${row.type_name}"?\n\nOnly do this if no drivers use it.`,
    );

    if (!confirmDelete) return;

    const { error } = await supabase.from("driver_types").delete().eq("id", row.id);

    if (error) {
      toast({
        title: "Driver type not deleted",
        description:
          "This driver type may already be used by drivers. You can make it inactive instead.",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Driver type deleted",
      description: `${row.type_name} has been removed.`,
    });

    await loadData();
  };

  const toggleActive = async (row: DriverTypeRow) => {
    const { data, error } = await supabase
      .from("driver_types")
      .update({ is_active: !row.is_active })
      .eq("id", row.id)
      .select();

    if (error) {
      toast({
        title: "Status not updated",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    if (!data || data.length === 0) {
      toast({
        title: "Status not updated",
        description:
          "The change did not reach the database. Please refresh and try again.",
        variant: "destructive",
      });
      return;
    }

    await loadData();
  };


  const renderRuleBadge = (label: string, active: boolean) => (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${
        active
          ? "border-green-700/50 bg-green-900/30 text-green-300"
          : "border-gray-700 bg-gray-900/40 text-gray-400"
      }`}
    >
      {label}
    </span>
  );

  const renderCheckbox = (field: keyof DriverTypeForm, label: string) => (
    <label className="flex items-center gap-2 rounded-lg border border-border bg-background/40 p-3 text-sm">
      <Checkbox
        checked={Boolean(form[field])}
        onCheckedChange={(checked) => updateForm(field, Boolean(checked) as never)}
      />
      <span>{label}</span>
    </label>
  );

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="border-b border-border">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-xl text-primary flex items-center gap-2">
              <BriefcaseBusiness className="w-5 h-5" />
              Driver Types
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Manage company-specific driver types and default payment rules.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Company: {tenant?.company_login_code || "TG&B"} — {tenant?.tenant_name || "Taxis Gold & Black"}
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <Button onClick={startAdd} className="golden-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Driver Type
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5 p-5">
        {showForm && (
          <div className="rounded-xl border border-yellow-900/30 bg-black/20 p-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-foreground">
                  {form.id ? "Edit Driver Type" : "Add Driver Type"}
                </h3>
                <p className="text-xs text-muted-foreground">
                  These rules become the default for drivers assigned to this type.
                </p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-yellow-500">Type Name *</Label>
                <Input
                  value={form.type_name}
                  onChange={(e) => updateForm("type_name", e.target.value)}
                  placeholder="e.g. Commission"
                  className="dark-input"
                />
              </div>

              <div>
                <Label className="text-yellow-500">Type Key</Label>
                <Input
                  value={form.type_key}
                  onChange={(e) => updateForm("type_key", e.target.value)}
                  placeholder="e.g. commission"
                  className="dark-input"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Used internally. Lowercase with underscores is best.
                </p>
              </div>

              <div>
                <Label className="text-yellow-500">Default Commission %</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.default_commission_percent}
                  onChange={(e) => updateForm("default_commission_percent", e.target.value)}
                  placeholder="Leave blank for no commission"
                  className="dark-input"
                />
              </div>

              <div className="lg:col-span-3">
                <Label className="text-yellow-500">Description</Label>
                <Input
                  value={form.description}
                  onChange={(e) => updateForm("description", e.target.value)}
                  placeholder="Short description of how this driver type works"
                  className="dark-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {renderCheckbox("is_waged", "Waged / employee driver")}
              {renderCheckbox("is_commission", "Uses commission")}
              {renderCheckbox("is_contractor", "Contractor")}
              {renderCheckbox("is_leased", "Leased / leaseholder")}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {renderCheckbox("uses_bonus_tiers", "Uses bonus tiers")}
              {renderCheckbox("uses_payment_type_percentages", "Uses payment type payable %")}
              {renderCheckbox("allows_deductions", "Allows deductions")}
              {renderCheckbox("is_active", "Active")}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button onClick={saveDriverType} disabled={saving} className="golden-button">
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Driver Type"}
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Driver Type</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Commission</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Classification</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Rules</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Status</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    Loading driver types...
                  </td>
                </tr>
              ) : sortedDriverTypes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    No driver types found. Add one to get started.
                  </td>
                </tr>
              ) : (
                sortedDriverTypes.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-b border-border last:border-b-0 ${!row.is_active ? "opacity-60" : ""}`}
                  >
                    <td className="px-3 py-3 align-top">
                      <div className="font-semibold text-foreground">{row.type_name}</div>
                      <div className="text-xs text-muted-foreground">{row.type_key}</div>
                      {row.description && (
                        <div className="mt-1 max-w-[320px] text-xs text-muted-foreground">
                          {row.description}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      {row.default_commission_percent === null || row.default_commission_percent === undefined ? (
                        <span className="text-muted-foreground">No default</span>
                      ) : (
                        <span className="font-semibold text-green-300">
                          {Number(row.default_commission_percent).toFixed(2)}%
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {renderRuleBadge("Waged", row.is_waged)}
                        {renderRuleBadge("Commission", row.is_commission)}
                        {renderRuleBadge("Contractor", row.is_contractor)}
                        {renderRuleBadge("Leased", row.is_leased)}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {renderRuleBadge("Bonus", row.uses_bonus_tiers)}
                        {renderRuleBadge("Payment %", row.uses_payment_type_percentages)}
                        {renderRuleBadge("Deductions", row.allows_deductions)}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <button
                        type="button"
                        onClick={() => toggleActive(row)}
                        className={`rounded-full border px-2 py-1 text-xs font-medium ${
                          row.is_active
                            ? "border-green-700/50 bg-green-900/30 text-green-300"
                            : "border-gray-700 bg-gray-900/40 text-gray-400"
                        }`}
                      >
                        {row.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => startEdit(row)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => deleteDriverType(row)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};


interface AdminDashboardProps {
  adminName: string;
  onLogout: () => void;
}

type BrandingSettings = {
  app_name: string;
  app_tagline: string;
  app_logo_url: string;
  app_icon_url: string;
  app_favicon_url: string;
};

const defaultBrandingSettings: BrandingSettings = {
  app_name: "DMS",
  app_tagline: "",
  app_logo_url: "",
  app_icon_url: "",
  app_favicon_url: "",
};

type ECardDefaultsSettings = {
  ecard_promo_min_qualifying_amount: string;
  ecard_promo_below_min_bonus_rate: string;
  ecard_promo_max_bonus_base: string;
  ecard_promo_bonus_type: string;
  ecard_promo_percent_rate: string;
  ecard_promo_base_amount: string;
  ecard_promo_bonus_per_base: string;
  ecard_promo_fixed_bonus_amount: string;
  ecard_promo_default_tag_color: string;
};

const defaultECardDefaultsSettings: ECardDefaultsSettings = {
  ecard_promo_min_qualifying_amount: "25",
  ecard_promo_below_min_bonus_rate: "0.50",
  ecard_promo_max_bonus_base: "150",
  ecard_promo_bonus_type: "PERCENT",
  ecard_promo_percent_rate: "1.00",
  ecard_promo_base_amount: "25",
  ecard_promo_bonus_per_base: "5",
  ecard_promo_fixed_bonus_amount: "10",
  ecard_promo_default_tag_color: "#3b82f6",
};

type FleetDefaultsSettings = {
  fleet_vehicle_inspection_warning_days: string;
  admin_inspection_interval_months: string;
  camera_check_interval_months: string;
  fleet_checks_due_soon_days: string;
  fleet_vehicle_check_interval_days: string;
  fleet_vehicle_check_shift_threshold: string;
  fleet_vehicle_check_require_odometer: string;
  fleet_vehicle_check_lockout_message: string;
  fleet_inspection_green_min_days: string;
  fleet_inspection_amber_warning_days: string;
  fleet_inspection_red_critical_days: string;
  fleet_cof_warning_days: string;
  fleet_cof_green_min_days: string;
  fleet_cof_amber_warning_days: string;
  fleet_cof_red_critical_days: string;
  fleet_rego_warning_days: string;
  fleet_rego_green_min_days: string;
  fleet_rego_amber_warning_days: string;
  fleet_rego_red_critical_days: string;
  fleet_rego_allowed_months: string;
  fleet_cof_allowed_months: string;
  fleet_cof_default_interval_months: string;
  fleet_cof_flex_months: string;
  fleet_service_warning_km: string;
  fleet_service_warning_days: string;
  fleet_maintenance_green_min_days: string;
  fleet_maintenance_amber_warning_days: string;
  fleet_maintenance_red_critical_days: string;
  fleet_maintenance_green_min_km: string;
  fleet_maintenance_amber_warning_km: string;
  fleet_maintenance_red_critical_km: string;
  fleet_ruc_warning_km: string;
  fleet_ruc_green_min_km: string;
  fleet_ruc_amber_warning_km: string;
  fleet_ruc_red_critical_km: string;
  fleet_vehicle_inactive_after_days: string;
  fleet_default_vehicle_type: string;
  fleet_default_status: string;
  fleet_default_fuel_type: string;
  fleet_green_min_days: string;
  fleet_amber_warning_days: string;
  fleet_red_critical_days: string;
  fleet_green_min_km: string;
  fleet_amber_warning_km: string;
  fleet_red_critical_km: string;
};

const defaultFleetDefaultsSettings: FleetDefaultsSettings = {
  fleet_vehicle_inspection_warning_days: "7",
  admin_inspection_interval_months: "3",
  camera_check_interval_months: "3",
  fleet_checks_due_soon_days: "14",
  fleet_vehicle_check_interval_days: "3",
  fleet_vehicle_check_shift_threshold: "3",
  fleet_vehicle_check_require_odometer: "true",
  fleet_vehicle_check_lockout_message:
    "Your vehicle check is now overdue. Complete a Vehicle Check to continue — other tabs, including submitting completed jobs for payment, stay locked until this is done.",
  fleet_inspection_green_min_days: "4",
  fleet_inspection_amber_warning_days: "3",
  fleet_inspection_red_critical_days: "1",
  fleet_cof_warning_days: "30",
  fleet_cof_green_min_days: "31",
  fleet_cof_amber_warning_days: "30",
  fleet_cof_red_critical_days: "10",
  fleet_rego_warning_days: "30",
  fleet_rego_green_min_days: "31",
  fleet_rego_amber_warning_days: "30",
  fleet_rego_red_critical_days: "10",
  fleet_rego_allowed_months: monthsToCsv(DEFAULT_REGO_ALLOWED_MONTHS),
  fleet_cof_allowed_months: monthsToCsv(DEFAULT_COF_ALLOWED_MONTHS),
  fleet_cof_default_interval_months: String(DEFAULT_COF_INTERVAL_MONTHS),
  fleet_cof_flex_months: String(DEFAULT_COF_FLEX_MONTHS),
  fleet_service_warning_km: "1000",
  fleet_service_warning_days: "30",
  fleet_maintenance_green_min_days: "31",
  fleet_maintenance_amber_warning_days: "30",
  fleet_maintenance_red_critical_days: "10",
  fleet_maintenance_green_min_km: "1001",
  fleet_maintenance_amber_warning_km: "1000",
  fleet_maintenance_red_critical_km: "250",
  fleet_ruc_warning_km: "1000",
  fleet_ruc_green_min_km: "1001",
  fleet_ruc_amber_warning_km: "1000",
  fleet_ruc_red_critical_km: "250",
  fleet_vehicle_inactive_after_days: "14",
  fleet_default_vehicle_type: "Car",
  fleet_default_status: "Active",
  fleet_default_fuel_type: "Petrol",
  fleet_green_min_days: "31",
  fleet_amber_warning_days: "30",
  fleet_red_critical_days: "10",
  fleet_green_min_km: "1001",
  fleet_amber_warning_km: "1000",
  fleet_red_critical_km: "250",
};

const AdminDashboard: React.FC<AdminDashboardProps> = ({
  adminName,
  onLogout,
}) => {
  const {
    hoistFeeRate,
    setHoistFeeRate,
    drivers,
    adminSettings,
    updateAdminSettings,
    currentUser,
    effectivePermissions,
  } = useAppContext();
  const { isModuleEnabled } = useTenantModules();

  const [activeTab, setActiveTab] = useState("closedcheck");
  const [settingsSubTab, setSettingsSubTab] = useState("branding");
  const [fleetDefaultsSubTab, setFleetDefaultsSubTab] = useState("fleet-setup");

  const [tempHoistFee, setTempHoistFee] = useState(hoistFeeRate.toString());
  const [tempInsuranceExcess, setTempInsuranceExcess] = useState(
    adminSettings.insuranceExcess.toString(),
  );
  const [tempCommissionPercent, setTempCommissionPercent] = useState(
    adminSettings.commissionPercent.toString(),
  );

  const [brandingSettings, setBrandingSettings] = useState<BrandingSettings>(
    defaultBrandingSettings,
  );

  const [eCardDefaultsSettings, setECardDefaultsSettings] =
    useState<ECardDefaultsSettings>(defaultECardDefaultsSettings);

  const [fleetDefaultsSettings, setFleetDefaultsSettings] =
    useState<FleetDefaultsSettings>(defaultFleetDefaultsSettings);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState<VehicleTypeRow[]>([]);

  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypeOptions);
  }, []);

  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [uploadingSetting, setUploadingSetting] = useState<string | null>(null);
  const [eCardDefaultsLoading, setECardDefaultsLoading] = useState(false);
  const [eCardDefaultsSaving, setECardDefaultsSaving] = useState(false);
  const [fleetDefaultsLoading, setFleetDefaultsLoading] = useState(false);
  const [fleetDefaultsSaving, setFleetDefaultsSaving] = useState(false);
  const [insuranceSettingsSubTab, setInsuranceSettingsSubTab] = useState("broker");

  const [showEmailReport, setShowEmailReport] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AdminNavDetail>).detail;
      if (detail?.tab) setActiveTab(detail.tab);
      // Settings' own sub-tab state lives directly on AdminDashboard, so it
      // can be set straight from the live event -- no sessionStorage
      // round-trip needed here (that's only for nested state owned by a
      // child component that might not be mounted yet, see adminNav.ts).
      if (detail?.tab === "settings" && detail.path?.[0]) {
        setSettingsSubTab(detail.path[0]);
        if (detail.path[0] === "fleet-defaults" && detail.path[1]) {
          setFleetDefaultsSubTab(detail.path[1]);
        }
        if (detail.path[0] === "insurance-settings" && detail.path[1]) {
          setInsuranceSettingsSubTab(detail.path[1]);
        }
      }
    };

    window.addEventListener(ADMIN_NAV_EVENT, handler);
    return () => window.removeEventListener(ADMIN_NAV_EVENT, handler);
  }, []);

  useEffect(() => {
    loadBrandingSettings();
    loadECardDefaultsSettings();
    loadFleetDefaultsSettings();
  }, []);

  const loadBrandingSettings = async () => {
    setBrandingLoading(true);

    const { data, error } = await supabase
      .from("settings")
      .select("name, value")
      .in("name", [
        "app_name",
        "app_tagline",
        "app_logo_url",
        "app_icon_url",
        "app_favicon_url",
      ]);

    setBrandingLoading(false);

    if (error) {
      toast({
        title: "Branding settings error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    const mapped = { ...defaultBrandingSettings };

    data?.forEach((setting) => {
      if (setting.name in mapped) {
        mapped[setting.name as keyof BrandingSettings] = setting.value || "";
      }
    });

    setBrandingSettings(mapped);
  };

  const updateBrandingField = (
    field: keyof BrandingSettings,
    value: string,
  ) => {
    setBrandingSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveBrandingSettings = async () => {
    setBrandingSaving(true);

    const rows = Object.entries(brandingSettings).map(([name, value]) => ({
      name,
      value,
      category: "Branding",
      is_active: true,
    }));

    const { error } = await supabase
      .from("settings")
      .upsert(rows, { onConflict: "name" });

    setBrandingSaving(false);

    if (error) {
      toast({
        title: "Branding not saved",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    if (brandingSettings.app_name) {
      document.title = brandingSettings.app_name;
    }

    if (brandingSettings.app_favicon_url) {
      const existingFavicon =
        document.querySelector<HTMLLinkElement>("link[rel='icon']") ||
        document.querySelector<HTMLLinkElement>("link[rel='shortcut icon']");

      const favicon = existingFavicon || document.createElement("link");

      favicon.rel = "icon";
      favicon.href = brandingSettings.app_favicon_url;

      if (!existingFavicon) {
        document.head.appendChild(favicon);
      }
    }

    toast({
      title: "Branding saved",
      description: "App branding settings updated successfully.",
    });
  };

  const uploadBrandingImage = async (
    file: File,
    field: keyof BrandingSettings,
  ) => {
    if (!file) return;

    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/svg+xml",
      "image/webp",
      "image/x-icon",
      "image/vnd.microsoft.icon",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Unsupported file",
        description: "Please upload a PNG, JPG, SVG, WEBP or ICO file.",
        variant: "destructive",
      });
      return;
    }

    setUploadingSetting(field);

    const fileExt = file.name.split(".").pop();
    const fileName = `${field}-${Date.now()}.${fileExt}`;
    const filePath = `branding/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("app-branding")
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: true,
      });

    setUploadingSetting(null);

    if (uploadError) {
      toast({
        title: "Upload failed",
        description: uploadError.message,
        variant: "destructive",
      });
      return;
    }

    const { data } = supabase.storage
      .from("app-branding")
      .getPublicUrl(filePath);

    updateBrandingField(field, data.publicUrl);

    toast({
      title: "Image uploaded",
      description: "Preview updated. Remember to save branding settings.",
    });
  };

  const loadECardDefaultsSettings = async () => {
    setECardDefaultsLoading(true);

    const { data, error } = await supabase
      .from("settings")
      .select("name, value")
      .in("name", [
        "ecard_promo_min_qualifying_amount",
        "ecard_promo_below_min_bonus_rate",
        "ecard_promo_max_bonus_base",
        "ecard_promo_bonus_type",
        "ecard_promo_percent_rate",
        "ecard_promo_base_amount",
        "ecard_promo_bonus_per_base",
        "ecard_promo_fixed_bonus_amount",
        "ecard_promo_default_tag_color",
      ]);

    setECardDefaultsLoading(false);

    if (error) {
      toast({
        title: "eCard defaults error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    const mapped = { ...defaultECardDefaultsSettings };

    data?.forEach((setting) => {
      if (setting.name in mapped) {
        mapped[setting.name as keyof ECardDefaultsSettings] =
          setting.value || "";
      }
    });

    setECardDefaultsSettings(mapped);
  };

  const updateECardDefaultField = (
    field: keyof ECardDefaultsSettings,
    value: string,
  ) => {
    setECardDefaultsSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveECardDefaultsSettings = async () => {
    setECardDefaultsSaving(true);

    const rows = [
      {
        name: "ecard_promo_min_qualifying_amount",
        value: eCardDefaultsSettings.ecard_promo_min_qualifying_amount,
        category: "eCard",
        label: "Minimum Qualifying Amount",
        description:
          "Default minimum top-up amount required for a promo bonus.",
        type: "number",
        sort_order: 10,
        is_active: true,
      },
      {
        name: "ecard_promo_below_min_bonus_rate",
        value: eCardDefaultsSettings.ecard_promo_below_min_bonus_rate,
        category: "eCard",
        label: "Below-Min Bonus Rate",
        description: "Default bonus rate for below-minimum top-ups, if used.",
        type: "number",
        sort_order: 20,
        is_active: true,
      },
      {
        name: "ecard_promo_max_bonus_base",
        value: eCardDefaultsSettings.ecard_promo_max_bonus_base,
        category: "eCard",
        label: "Maximum Bonus Base",
        description:
          "Default maximum top-up amount used when calculating bonus.",
        type: "number",
        sort_order: 30,
        is_active: true,
      },
      {
        name: "ecard_promo_bonus_type",
        value: eCardDefaultsSettings.ecard_promo_bonus_type,
        category: "eCard",
        label: "Default Bonus Type",
        description:
          "Default promo bonus type: PERCENT, PER_BASE_AMOUNT, or FIXED_AMOUNT.",
        type: "text",
        sort_order: 40,
        is_active: true,
      },
      {
        name: "ecard_promo_percent_rate",
        value: eCardDefaultsSettings.ecard_promo_percent_rate,
        category: "eCard",
        label: "Default Percent Rate",
        description: "Default percentage bonus rate. 1.00 means 100%.",
        type: "number",
        sort_order: 50,
        is_active: true,
      },
      {
        name: "ecard_promo_base_amount",
        value: eCardDefaultsSettings.ecard_promo_base_amount,
        category: "eCard",
        label: "Default Base Amount",
        description: "Default base amount for per-base promo calculations.",
        type: "number",
        sort_order: 60,
        is_active: true,
      },
      {
        name: "ecard_promo_bonus_per_base",
        value: eCardDefaultsSettings.ecard_promo_bonus_per_base,
        category: "eCard",
        label: "Default Bonus Per Base",
        description: "Default bonus amount per base amount.",
        type: "number",
        sort_order: 70,
        is_active: true,
      },
      {
        name: "ecard_promo_fixed_bonus_amount",
        value: eCardDefaultsSettings.ecard_promo_fixed_bonus_amount,
        category: "eCard",
        label: "Default Fixed Bonus Amount",
        description: "Default flat bonus amount for fixed bonus promotions.",
        type: "number",
        sort_order: 80,
        is_active: true,
      },
      {
        name: "ecard_promo_default_tag_color",
        value: eCardDefaultsSettings.ecard_promo_default_tag_color,
        category: "eCard",
        label: "Default Promo Tag Colour",
        description: "Default colour used when creating new promo tags.",
        type: "text",
        sort_order: 90,
        is_active: true,
      },
    ];

    const { error } = await supabase
      .from("settings")
      .upsert(rows, { onConflict: "name" });

    setECardDefaultsSaving(false);

    if (error) {
      toast({
        title: "eCard defaults not saved",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "eCard defaults saved",
      description: "New promo defaults have been updated successfully.",
    });
  };

  const loadFleetDefaultsSettings = async () => {
    setFleetDefaultsLoading(true);

    const { data, error } = await supabase
      .from("settings")
      .select("name, value")
      .in("name", [
        "fleet_vehicle_inspection_warning_days",
        "admin_inspection_interval_months",
        "camera_check_interval_months",
        "fleet_checks_due_soon_days",
        "fleet_vehicle_check_interval_days",
        "fleet_vehicle_check_shift_threshold",
        "fleet_vehicle_check_require_odometer",
        "fleet_vehicle_check_lockout_message",
        "fleet_inspection_green_min_days",
        "fleet_inspection_amber_warning_days",
        "fleet_inspection_red_critical_days",
        "fleet_cof_warning_days",
        "fleet_cof_green_min_days",
        "fleet_cof_amber_warning_days",
        "fleet_cof_red_critical_days",
        "fleet_rego_warning_days",
        "fleet_rego_green_min_days",
        "fleet_rego_amber_warning_days",
        "fleet_rego_red_critical_days",
        "fleet_rego_allowed_months",
        "fleet_cof_allowed_months",
        "fleet_cof_default_interval_months",
        "fleet_cof_flex_months",
        "fleet_service_warning_km",
        "fleet_service_warning_days",
        "fleet_maintenance_green_min_days",
        "fleet_maintenance_amber_warning_days",
        "fleet_maintenance_red_critical_days",
        "fleet_maintenance_green_min_km",
        "fleet_maintenance_amber_warning_km",
        "fleet_maintenance_red_critical_km",
        "fleet_ruc_warning_km",
        "fleet_ruc_green_min_km",
        "fleet_ruc_amber_warning_km",
        "fleet_ruc_red_critical_km",
        "fleet_vehicle_inactive_after_days",
        "fleet_default_vehicle_type",
        "fleet_default_status",
        "fleet_default_fuel_type",
        "fleet_green_min_days",
        "fleet_amber_warning_days",
        "fleet_red_critical_days",
        "fleet_green_min_km",
        "fleet_amber_warning_km",
        "fleet_red_critical_km",
      ]);

    setFleetDefaultsLoading(false);

    if (error) {
      toast({
        title: "Fleet defaults error",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    const mapped = { ...defaultFleetDefaultsSettings };

    data?.forEach((setting) => {
      if (setting.name in mapped) {
        mapped[setting.name as keyof FleetDefaultsSettings] =
          setting.value || "";
      }
    });

    setFleetDefaultsSettings(mapped);
  };

  const updateFleetDefaultField = (
    field: keyof FleetDefaultsSettings,
    value: string,
  ) => {
    setFleetDefaultsSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveFleetDefaultsSettings = async () => {
    setFleetDefaultsSaving(true);

    const rows = [
      {
        name: "fleet_vehicle_inspection_warning_days",
        value: fleetDefaultsSettings.fleet_vehicle_inspection_warning_days,
        category: "Fleet",
        label: "Vehicle Inspection Warning Days",
        description:
          "Warn when vehicle inspection is due within this many days.",
        type: "number",
        sort_order: 10,
        is_active: true,
      },
      {
        name: "admin_inspection_interval_months",
        value: fleetDefaultsSettings.admin_inspection_interval_months,
        category: "Fleet",
        label: "Admin Inspection Every (months)",
        description:
          "A vehicle is due an admin inspection this many months after its last one. Can be overridden per vehicle.",
        type: "number",
        sort_order: 20.1,
        is_active: true,
      },
      {
        name: "camera_check_interval_months",
        value: fleetDefaultsSettings.camera_check_interval_months,
        category: "Fleet",
        label: "Camera Check Every (months)",
        description:
          "A vehicle's camera is due a check this many months after its last one. Can be overridden per vehicle.",
        type: "number",
        sort_order: 20.2,
        is_active: true,
      },
      {
        name: "fleet_checks_due_soon_days",
        value: fleetDefaultsSettings.fleet_checks_due_soon_days,
        category: "Fleet",
        label: "Checks \"Due Soon\" Window (days)",
        description:
          "Admin inspections and camera checks show as due soon, and appear on Closed Check, this many days before they fall due.",
        type: "number",
        sort_order: 20.3,
        is_active: true,
      },
      {
        name: "fleet_vehicle_check_interval_days",
        value: fleetDefaultsSettings.fleet_vehicle_check_interval_days,
        category: "Fleet",
        label: "Fleet Check Interval Days",
        description:
          "A vehicle check becomes due once this many days have passed since its last check. Example: 1 means a check is due every day.",
        type: "number",
        sort_order: 11,
        is_active: true,
      },
      {
        name: "fleet_vehicle_check_shift_threshold",
        value: fleetDefaultsSettings.fleet_vehicle_check_shift_threshold,
        category: "Fleet",
        label: "Fleet Check Shift Threshold",
        description:
          "A vehicle check also becomes due once this many completed shifts have happened since its last check, even if the day interval above hasn't been reached yet. Whichever trigger fires first wins.",
        type: "number",
        sort_order: 11.5,
        is_active: true,
      },
      {
        name: "fleet_vehicle_check_require_odometer",
        value: fleetDefaultsSettings.fleet_vehicle_check_require_odometer,
        category: "Fleet",
        label: "Require Odometer Reading",
        description:
          "Require drivers to type in the odometer reading during a Vehicle Check, not just photograph it. When on, a check can't be submitted without it, and the reading updates the vehicle's last known KM (used for RUC/service/tyre-rotation tracking) whenever it's higher than what's currently on file.",
        type: "boolean",
        sort_order: 11.6,
        is_active: true,
      },
      {
        name: "fleet_vehicle_check_lockout_message",
        value: fleetDefaultsSettings.fleet_vehicle_check_lockout_message,
        category: "Fleet",
        label: "Fleet Check Lockout Message",
        description:
          "Shown to a driver once their vehicle check is overdue -- every tab except Vehicle Check is blocked until they complete one.",
        type: "text",
        sort_order: 11.7,
        is_active: true,
      },
      {
        name: "fleet_inspection_green_min_days",
        value: fleetDefaultsSettings.fleet_inspection_green_min_days,
        category: "Fleet",
        label: "Inspection Green Minimum Days",
        description:
          "Fleet inspections with more than this many days remaining are shown as green.",
        type: "number",
        sort_order: 12,
        is_active: true,
      },
      {
        name: "fleet_inspection_amber_warning_days",
        value: fleetDefaultsSettings.fleet_inspection_amber_warning_days,
        category: "Fleet",
        label: "Inspection Amber Warning Days",
        description:
          "Fleet inspections with this many days or fewer remaining are shown as amber.",
        type: "number",
        sort_order: 13,
        is_active: true,
      },
      {
        name: "fleet_inspection_red_critical_days",
        value: fleetDefaultsSettings.fleet_inspection_red_critical_days,
        category: "Fleet",
        label: "Inspection Red Critical Days",
        description:
          "Fleet inspections with this many days or fewer remaining are shown as red.",
        type: "number",
        sort_order: 14,
        is_active: true,
      },
      {
        name: "fleet_cof_warning_days",
        value: fleetDefaultsSettings.fleet_cof_warning_days,
        category: "Fleet",
        label: "COF Warning Days",
        description:
          "Warn when Certificate of Fitness expires within this many days.",
        type: "number",
        sort_order: 20,
        is_active: true,
      },
      {
        name: "fleet_rego_warning_days",
        value: fleetDefaultsSettings.fleet_rego_warning_days,
        category: "Fleet",
        label: "Registration Warning Days",
        description:
          "Warn when vehicle registration expires within this many days.",
        type: "number",
        sort_order: 30,
        is_active: true,
      },
      {
        name: "fleet_rego_allowed_months",
        value: fleetDefaultsSettings.fleet_rego_allowed_months,
        category: "Fleet",
        label: "Rego Allowed Months",
        description:
          "Months registration is allowed to be renewed in, so renewals can be spread evenly across them instead of clustering (e.g. exclude December/January to avoid a big outgoing over the holidays).",
        type: "text",
        sort_order: 45,
        is_active: true,
      },
      {
        name: "fleet_cof_allowed_months",
        value: fleetDefaultsSettings.fleet_cof_allowed_months,
        category: "Fleet",
        label: "COF Allowed Months",
        description: "Months COF/WOF is allowed to be booked in, same idea as Rego Allowed Months.",
        type: "text",
        sort_order: 46,
        is_active: true,
      },
      {
        name: "fleet_cof_default_interval_months",
        value: fleetDefaultsSettings.fleet_cof_default_interval_months,
        category: "Fleet",
        label: "COF Default Interval (months)",
        description:
          "Default renewal interval for COF/WOF (e.g. 6 for COF, 12 for WOF). Override per vehicle on its Fleet tab for anything on a different schedule.",
        type: "number",
        sort_order: 47,
        is_active: true,
      },
      {
        name: "fleet_cof_flex_months",
        value: fleetDefaultsSettings.fleet_cof_flex_months,
        category: "Fleet",
        label: "COF Flex Months",
        description:
          "How many months early a COF/WOF inspection can be booked to land in a lighter month, without losing time off the next cycle. Never suggests booking late.",
        type: "number",
        sort_order: 48,
        is_active: true,
      },
      {
        name: "fleet_service_warning_km",
        value: fleetDefaultsSettings.fleet_service_warning_km,
        category: "Fleet",
        label: "Service Warning KM",
        description:
          "Warn when vehicle is within this many kilometres of service due.",
        type: "number",
        sort_order: 40,
        is_active: true,
      },
      {
        name: "fleet_service_warning_days",
        value: fleetDefaultsSettings.fleet_service_warning_days,
        category: "Fleet",
        label: "Service Warning Days",
        description: "Warn when vehicle service is due within this many days.",
        type: "number",
        sort_order: 50,
        is_active: true,
      },
      {
        name: "fleet_ruc_warning_km",
        value: fleetDefaultsSettings.fleet_ruc_warning_km,
        category: "Fleet",
        label: "RUC Warning KM",
        description:
          "Warn when vehicle is within this many kilometres of RUC expiry.",
        type: "number",
        sort_order: 60,
        is_active: true,
      },
      {
        name: "fleet_vehicle_inactive_after_days",
        value: fleetDefaultsSettings.fleet_vehicle_inactive_after_days,
        category: "Fleet",
        label: "Vehicle Inactive After Days",
        description:
          "Flag a vehicle as inactive if it has not been updated or used within this many days.",
        type: "number",
        sort_order: 70,
        is_active: true,
      },
      {
        name: "fleet_default_vehicle_type",
        value: fleetDefaultsSettings.fleet_default_vehicle_type,
        category: "Fleet",
        label: "Default Vehicle Type",
        description: "Default vehicle type when creating a new vehicle.",
        type: "text",
        sort_order: 80,
        is_active: true,
      },
      {
        name: "fleet_default_status",
        value: fleetDefaultsSettings.fleet_default_status,
        category: "Fleet",
        label: "Default Vehicle Status",
        description: "Default status applied to new vehicles.",
        type: "text",
        sort_order: 90,
        is_active: true,
      },
      {
        name: "fleet_default_fuel_type",
        value: fleetDefaultsSettings.fleet_default_fuel_type,
        category: "Fleet",
        label: "Default Fuel Type",
        description: "Default fuel type for new vehicles.",
        type: "text",
        sort_order: 100,
        is_active: true,
      },
      {
        name: "fleet_cof_green_min_days",
        value: fleetDefaultsSettings.fleet_cof_green_min_days,
        category: "Fleet",
        label: "COF Green Minimum Days",
        description: "COF with more than this many days remaining is green.",
        type: "number",
        sort_order: 21,
        is_active: true,
      },
      {
        name: "fleet_cof_amber_warning_days",
        value: fleetDefaultsSettings.fleet_cof_amber_warning_days,
        category: "Fleet",
        label: "COF Amber Warning Days",
        description: "COF with this many days or fewer remaining is amber.",
        type: "number",
        sort_order: 22,
        is_active: true,
      },
      {
        name: "fleet_cof_red_critical_days",
        value: fleetDefaultsSettings.fleet_cof_red_critical_days,
        category: "Fleet",
        label: "COF Red Critical Days",
        description: "COF with this many days or fewer remaining is red.",
        type: "number",
        sort_order: 23,
        is_active: true,
      },
      {
        name: "fleet_rego_green_min_days",
        value: fleetDefaultsSettings.fleet_rego_green_min_days,
        category: "Fleet",
        label: "Rego Green Minimum Days",
        description: "Registration with more than this many days remaining is green.",
        type: "number",
        sort_order: 31,
        is_active: true,
      },
      {
        name: "fleet_rego_amber_warning_days",
        value: fleetDefaultsSettings.fleet_rego_amber_warning_days,
        category: "Fleet",
        label: "Rego Amber Warning Days",
        description: "Registration with this many days or fewer remaining is amber.",
        type: "number",
        sort_order: 32,
        is_active: true,
      },
      {
        name: "fleet_rego_red_critical_days",
        value: fleetDefaultsSettings.fleet_rego_red_critical_days,
        category: "Fleet",
        label: "Rego Red Critical Days",
        description: "Registration with this many days or fewer remaining is red.",
        type: "number",
        sort_order: 33,
        is_active: true,
      },
      {
        name: "fleet_maintenance_green_min_days",
        value: fleetDefaultsSettings.fleet_maintenance_green_min_days,
        category: "Fleet",
        label: "Maintenance Green Minimum Days",
        description: "Service dates with more than this many days remaining are green.",
        type: "number",
        sort_order: 51,
        is_active: true,
      },
      {
        name: "fleet_maintenance_amber_warning_days",
        value: fleetDefaultsSettings.fleet_maintenance_amber_warning_days,
        category: "Fleet",
        label: "Maintenance Amber Warning Days",
        description: "Service dates with this many days or fewer remaining are amber.",
        type: "number",
        sort_order: 52,
        is_active: true,
      },
      {
        name: "fleet_maintenance_red_critical_days",
        value: fleetDefaultsSettings.fleet_maintenance_red_critical_days,
        category: "Fleet",
        label: "Maintenance Red Critical Days",
        description: "Service dates with this many days or fewer remaining are red.",
        type: "number",
        sort_order: 53,
        is_active: true,
      },
      {
        name: "fleet_maintenance_green_min_km",
        value: fleetDefaultsSettings.fleet_maintenance_green_min_km,
        category: "Fleet",
        label: "Maintenance Green Minimum KM",
        description: "Service kilometre buffers above this amount are green.",
        type: "number",
        sort_order: 54,
        is_active: true,
      },
      {
        name: "fleet_maintenance_amber_warning_km",
        value: fleetDefaultsSettings.fleet_maintenance_amber_warning_km,
        category: "Fleet",
        label: "Maintenance Amber Warning KM",
        description: "Service kilometre buffers at or below this amount are amber.",
        type: "number",
        sort_order: 55,
        is_active: true,
      },
      {
        name: "fleet_maintenance_red_critical_km",
        value: fleetDefaultsSettings.fleet_maintenance_red_critical_km,
        category: "Fleet",
        label: "Maintenance Red Critical KM",
        description: "Service kilometre buffers at or below this amount are red.",
        type: "number",
        sort_order: 56,
        is_active: true,
      },
      {
        name: "fleet_ruc_green_min_km",
        value: fleetDefaultsSettings.fleet_ruc_green_min_km,
        category: "Fleet",
        label: "RUC Green Minimum KM",
        description: "RUC kilometre buffers above this amount are green.",
        type: "number",
        sort_order: 61,
        is_active: true,
      },
      {
        name: "fleet_ruc_amber_warning_km",
        value: fleetDefaultsSettings.fleet_ruc_amber_warning_km,
        category: "Fleet",
        label: "RUC Amber Warning KM",
        description: "RUC kilometre buffers at or below this amount are amber.",
        type: "number",
        sort_order: 62,
        is_active: true,
      },
      {
        name: "fleet_ruc_red_critical_km",
        value: fleetDefaultsSettings.fleet_ruc_red_critical_km,
        category: "Fleet",
        label: "RUC Red Critical KM",
        description: "RUC kilometre buffers at or below this amount are red.",
        type: "number",
        sort_order: 63,
        is_active: true,
      },
      {
        name: "fleet_green_min_days",
        value: fleetDefaultsSettings.fleet_green_min_days,
        category: "Fleet",
        label: "Green Minimum Days",
        description:
          "Items with more than this many days remaining are shown as green.",
        type: "number",
        sort_order: 110,
        is_active: true,
      },
      {
        name: "fleet_amber_warning_days",
        value: fleetDefaultsSettings.fleet_amber_warning_days,
        category: "Fleet",
        label: "Amber Warning Days",
        description:
          "Items with this many days or fewer remaining are shown as amber.",
        type: "number",
        sort_order: 120,
        is_active: true,
      },
      {
        name: "fleet_red_critical_days",
        value: fleetDefaultsSettings.fleet_red_critical_days,
        category: "Fleet",
        label: "Red Critical Days",
        description:
          "Items with this many days or fewer remaining are shown as red.",
        type: "number",
        sort_order: 130,
        is_active: true,
      },
      {
        name: "fleet_green_min_km",
        value: fleetDefaultsSettings.fleet_green_min_km,
        category: "Fleet",
        label: "Green Minimum KM",
        description:
          "Items with more than this many kilometres remaining are shown as green.",
        type: "number",
        sort_order: 140,
        is_active: true,
      },
      {
        name: "fleet_amber_warning_km",
        value: fleetDefaultsSettings.fleet_amber_warning_km,
        category: "Fleet",
        label: "Amber Warning KM",
        description:
          "Items with this many kilometres or fewer remaining are shown as amber.",
        type: "number",
        sort_order: 150,
        is_active: true,
      },
      {
        name: "fleet_red_critical_km",
        value: fleetDefaultsSettings.fleet_red_critical_km,
        category: "Fleet",
        label: "Red Critical KM",
        description:
          "Items with this many kilometres or fewer remaining are shown as red.",
        type: "number",
        sort_order: 160,
        is_active: true,
      },
    ];

    const { error } = await supabase
      .from("settings")
      .upsert(rows, { onConflict: "name" });

    setFleetDefaultsSaving(false);

    if (error) {
      toast({
        title: "Fleet defaults not saved",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Fleet defaults saved",
      description:
        "Fleet and compliance default settings have been updated successfully.",
    });
  };

  const updateHoistFee = () => {
    const newRate = parseFloat(tempHoistFee);

    if (isNaN(newRate) || newRate < 0) {
      toast({
        title: "Error",
        description: "Please enter a valid hoist fee rate",
      });
      return;
    }

    setHoistFeeRate(newRate);

    toast({
      title: "Success",
      description: `Hoist fee rate updated to $${newRate.toFixed(2)}`,
    });
  };

  const updateSettings = async () => {
    const insuranceExcess = parseFloat(tempInsuranceExcess);
    const commissionPercent = parseFloat(tempCommissionPercent);
    const hoistFee = parseFloat(tempHoistFee);

    if (
      isNaN(insuranceExcess) ||
      isNaN(commissionPercent) ||
      isNaN(hoistFee)
    ) {
      toast({
        title: "Error",
        description: "Please enter valid numbers for all settings",
      });
      return;
    }

    updateAdminSettings({
      insuranceExcess,
      commissionPercent,
    });

    setHoistFeeRate(hoistFee);

    const { error } = await supabase.from("settings").upsert(
      [
        {
          name: "insurance_excess",
          value: insuranceExcess.toString(),
          category: "Payments",
          label: "Insurance Excess",
          description:
            "Default insurance excess deducted from driver payments.",
          type: "number",
          sort_order: 10,
          is_active: true,
        },
        {
          name: "commission_percent",
          value: commissionPercent.toString(),
          category: "Payments",
          label: "Commission Percentage",
          description: "Default commission percentage used for calculations.",
          type: "number",
          sort_order: 20,
          is_active: true,
        },
        {
          name: "tm_hoist",
          value: hoistFee.toString(),
          category: "Payments",
          label: "TM Hoist Fee",
          description: "Default Total Mobility hoist fee.",
          type: "number",
          sort_order: 40,
          is_active: true,
        },
      ],
      { onConflict: "name" },
    );

    if (error) {
      toast({
        title: "Settings saved locally, but not to database",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Payment settings updated successfully.",
    });
  };

  const renderImageSetting = (
    field: keyof BrandingSettings,
    label: string,
    description: string,
    previewClassName = "h-20",
  ) => {
    const value = brandingSettings[field];

    return (
      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <div>
          <Label className="text-foreground">{label}</Label>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>

        {value ? (
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <img
              src={value}
              alt={label}
              className={`${previewClassName} object-contain`}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground">
            No image uploaded yet
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-2">
          <Input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp,image/x-icon"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadBrandingImage(file, field);
            }}
            className="dark-input"
          />

          <Button
            type="button"
            variant="outline"
            disabled={uploadingSetting === field}
            className="whitespace-nowrap"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploadingSetting === field ? "Uploading..." : "Upload"}
          </Button>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground">Image URL</Label>
          <Input
            value={value}
            onChange={(e) => updateBrandingField(field, e.target.value)}
            placeholder="Or paste an image URL"
            className="dark-input mt-1"
          />
        </div>
      </div>
    );
  };

  const renderECardNumberSetting = (
    field: keyof ECardDefaultsSettings,
    label: string,
    description: string,
    step = "0.01",
  ) => {
    return (
      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <div>
          <Label className="text-foreground">{label}</Label>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>

        <Input
          type="number"
          step={step}
          value={eCardDefaultsSettings[field]}
          onChange={(e) => updateECardDefaultField(field, e.target.value)}
          className="dark-input"
        />
      </div>
    );
  };

  const renderFleetNumberSetting = (
    field: keyof FleetDefaultsSettings,
    label: string,
    description: string,
    step = "1",
  ) => {
    return (
      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <div>
          <Label className="text-foreground">{label}</Label>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>

        <Input
          type="number"
          step={step}
          value={fleetDefaultsSettings[field]}
          onChange={(e) => updateFleetDefaultField(field, e.target.value)}
          className="dark-input"
        />
      </div>
    );
  };



  const renderFleetSwitchSetting = (
    field: keyof FleetDefaultsSettings,
    label: string,
    description: string,
  ) => {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/40 p-4">
        <div>
          <Label className="text-foreground">{label}</Label>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>
        <Switch
          checked={fleetDefaultsSettings[field] === "true"}
          onCheckedChange={(checked) => updateFleetDefaultField(field, checked ? "true" : "false")}
        />
      </div>
    );
  };

  const renderFleetDayTrafficSettings = (
    greenField: keyof FleetDefaultsSettings,
    amberField: keyof FleetDefaultsSettings,
    redField: keyof FleetDefaultsSettings,
    title = "Traffic Light Days",
  ) => (
    <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-4">
      <div>
        <h4 className="font-medium text-foreground">{title}</h4>
        <p className="text-xs text-muted-foreground mt-1">
          Set when this area shows as green, amber, or red.
        </p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {renderFleetNumberSetting(
          greenField,
          "Green Minimum Days",
          "More than this many days remaining is green.",
        )}
        {renderFleetNumberSetting(
          amberField,
          "Amber Warning Days",
          "This many days or fewer remaining is amber.",
        )}
        {renderFleetNumberSetting(
          redField,
          "Red Critical Days",
          "This many days or fewer remaining is red.",
        )}
      </div>
    </div>
  );

  const renderFleetKmTrafficSettings = (
    greenField: keyof FleetDefaultsSettings,
    amberField: keyof FleetDefaultsSettings,
    redField: keyof FleetDefaultsSettings,
    title = "Traffic Light KM",
  ) => (
    <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-4">
      <div>
        <h4 className="font-medium text-foreground">{title}</h4>
        <p className="text-xs text-muted-foreground mt-1">
          Set kilometre-based green, amber, and red thresholds.
        </p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {renderFleetNumberSetting(
          greenField,
          "Green Minimum KM",
          "More than this many kilometres remaining is green.",
        )}
        {renderFleetNumberSetting(
          amberField,
          "Amber Warning KM",
          "This many kilometres or fewer remaining is amber.",
        )}
        {renderFleetNumberSetting(
          redField,
          "Red Critical KM",
          "This many kilometres or fewer remaining is red.",
        )}
      </div>
    </div>
  );

  // 12 toggle buttons storing a CSV of month numbers (1-12) into a
  // FleetDefaultsSettings text field -- used for Rego/COF Allowed Months.
  const renderFleetMonthsSetting = (
    field: keyof FleetDefaultsSettings,
    label: string,
    description: string,
  ) => {
    const selected = parseMonthsCsv(fleetDefaultsSettings[field], []);
    const toggle = (month: number) => {
      const next = selected.includes(month) ? selected.filter((m) => m !== month) : [...selected, month];
      updateFleetDefaultField(field, monthsToCsv(next));
    };
    return (
      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4 xl:col-span-2">
        <div>
          <Label className="text-foreground">{label}</Label>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
            <button
              key={month}
              type="button"
              onClick={() => toggle(month)}
              className={`rounded-md border px-2 py-1.5 text-sm font-medium transition-colors ${
                selected.includes(month)
                  ? "bg-yellow-500 border-yellow-500 text-black"
                  : "border-border bg-background/60 text-muted-foreground hover:bg-background"
              }`}
            >
              {monthLabel(month - 1)}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderFleetTextSetting = (
    field: keyof FleetDefaultsSettings,
    label: string,
    description: string,
    options?: string[],
    multiline?: boolean,
  ) => {
    return (
      <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
        <div>
          <Label className="text-foreground">{label}</Label>
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        </div>

        {options?.length ? (
          <select
            value={fleetDefaultsSettings[field]}
            onChange={(e) => updateFleetDefaultField(field, e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
          >
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : multiline ? (
          <Textarea
            value={fleetDefaultsSettings[field]}
            onChange={(e) => updateFleetDefaultField(field, e.target.value)}
            className="dark-input min-h-24"
          />
        ) : (
          <Input
            value={fleetDefaultsSettings[field]}
            onChange={(e) => updateFleetDefaultField(field, e.target.value)}
            className="dark-input"
          />
        )}
      </div>
    );
  };

  // moduleKey ties a tab to one of the paid catalog modules a superadmin
  // can turn on/off per tenant (SuperadminDashboard.tsx / tenant_modules) --
  // omitted (undefined) on a tab deliberately left ungated for now rather
  // than guessed at, since hiding the wrong tab is worse than a tab not
  // yet being gated. Dashboard and Settings are base-app, not paid modules,
  // so their own tabs are always ungated -- but Settings' sub-tabs (below)
  // are still gated individually, so switching a module off does hide that
  // module's own settings even though the Settings tab itself stays put.
  const mainTabs = [
    { value: "closedcheck", label: "Closed Check", icon: CheckCircle2, moduleKey: "payroll" },
    { value: "matchhub", label: "Match Hub", icon: GitMerge, moduleKey: undefined },
    { value: "summary", label: "Summary", icon: BarChart3, moduleKey: undefined },
    { value: "timesheets", label: "Timesheets", icon: Clock3, moduleKey: "payroll" },
    { value: "drivers", label: "Drivers", icon: Users, moduleKey: "drivers" },
    { value: "fleet", label: "Fleet", icon: Car, moduleKey: "fleet" },
    { value: "fleetpulse", label: "Fleet Pulse", icon: Activity, moduleKey: "fleet" },
    { value: "fleetcapacity", label: "Fleet Capacity", icon: Gauge, moduleKey: "fleet" },
    { value: "roster", label: "Roster", icon: CalendarDays, moduleKey: "roster" },
    { value: "jobinput", label: "Job Input", icon: ClipboardPlus, moduleKey: undefined },
    { value: "reports", label: "Reports", icon: FileText, moduleKey: "reports" },
    { value: "crm", label: "CRM", icon: Building2, moduleKey: "crm" },
    { value: "exportbuilder", label: "Export Builder", icon: FileSpreadsheet, moduleKey: "reports" },
    { value: "ecard", label: "eCard", icon: CreditCard, moduleKey: "ecards" },
    { value: "payout", label: "Payout Report", icon: WalletCards, moduleKey: "payroll" },
    { value: "insurance", label: "Insurance", icon: ShieldAlert, moduleKey: "fleet" },
    { value: "ivrcalls", label: "IVR Calls", icon: PhoneCall, moduleKey: "ivr" },
    { value: "commsbook", label: "Comms Book", icon: MessageSquare, moduleKey: "drivers" },
    // Settings is base-app, not a paid module -- always available regardless
    // of tenant_modules (per Janelle: Dashboard & Settings shouldn't be
    // toggleable). Individual settings sub-tabs are still gated by their
    // own module below, so turning a module off does hide that module's
    // settings even though the Settings tab itself always shows.
    { value: "settings", label: "Settings", icon: Settings, moduleKey: undefined },
  ];

  // Settings' own sub-tabs, individually gated by module -- Site Map,
  // Company, Branding and Team Access stay ungated since they're base-app
  // config every tenant needs regardless of which paid modules they have.
  const settingsSubTabCatalog = [
    { value: "sitemap", label: "Site Map", icon: MapIcon, moduleKey: undefined },
    { value: "company", label: "Company", icon: Building2, moduleKey: undefined },
    { value: "branding", label: "Branding", icon: Palette, moduleKey: undefined },
    { value: "payment-types", label: "Payment Types", icon: CreditCardIcon, moduleKey: "payroll" },
    { value: "payment-builder", label: "Payment Builder", icon: LayoutTemplate, moduleKey: "payroll" },
    { value: "driver-types", label: "Driver Types", icon: UserCog, moduleKey: "drivers" },
    { value: "payment-settings", label: "Payment Settings", icon: SlidersHorizontal, moduleKey: "payroll" },
    { value: "ecard-defaults", label: "eCard Defaults", icon: BadgeDollarSign, moduleKey: "ecards" },
    { value: "fleet-defaults", label: "Fleet Defaults", icon: ShieldCheck, moduleKey: "fleet" },
    { value: "insurance-settings", label: "Insurance Settings", icon: ShieldAlert, moduleKey: "fleet" },
    { value: "bonus-tiers", label: "Bonus Tiers", icon: Gift, moduleKey: "payroll" },
    { value: "roster-positions", label: "Roster Positions", icon: BriefcaseBusiness, moduleKey: "roster" },
    { value: "vehicle-capacity", label: "Vehicle Types", icon: CalendarClock, moduleKey: "roster" },
    { value: "roster-groups", label: "Roster Groups", icon: UsersRound, moduleKey: "roster" },
    { value: "roster-holidays", label: "Roster Holidays", icon: CalendarClock, moduleKey: "roster" },
    { value: "work-time-rules", label: "Work Time Rules", icon: Timer, moduleKey: "roster" },
    { value: "maintenance-lists", label: "Maintenance Lists", icon: Wrench, moduleKey: "fleet" },
    { value: "ivr", label: "IVR", icon: Phone, moduleKey: "ivr" },
    { value: "driver-notices", label: "Driver Notices", icon: Megaphone, moduleKey: "drivers" },
    { value: "taxicaller-companies", label: "TaxiCaller", icon: Webhook, moduleKey: "fleet" },
    { value: "team-access", label: "Team Access", icon: Shield, moduleKey: undefined },
  ];

  // Unrestricted for every current admin/driver (no permission-group
  // assignment) -- see src/lib/permissions.ts. Only someone explicitly
  // assigned a group (Team Access in Settings) gets cut down to this.
  // Module gating (tenant_modules, set by a superadmin) is a second,
  // independent filter on top -- a tab only shows when BOTH the user's own
  // permissions allow it AND the tenant's own plan includes that module.
  const visibleMainTabs = mainTabs.filter(
    (t) => canViewTab(effectivePermissions, t.value) && isModuleEnabled(t.moduleKey)
  );

  // The default tab ("closedcheck") is an internal tool with no restricted
  // mapping, so a restricted user would otherwise land on a tab they can't
  // see -- redirect to the first one they're actually allowed.
  useEffect(() => {
    if (effectivePermissions.isUnrestricted) return;
    if (visibleMainTabs.some((t) => t.value === activeTab)) return;
    if (visibleMainTabs.length > 0) setActiveTab(visibleMainTabs[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePermissions, activeTab]);

  const mainTabClass =
    "group relative mb-[-1px] h-[58px] w-[54px] shrink-0 overflow-hidden rounded-t-xl border border-b-0 border-yellow-900/30 bg-gradient-to-b from-zinc-900 via-zinc-950 to-black px-1.5 py-2 text-yellow-500 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_8px_18px_rgba(0,0,0,0.35)] transition-all duration-200 hover:-translate-y-[1px] hover:border-yellow-500/50 hover:from-zinc-800 hover:text-yellow-300 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_10px_22px_rgba(0,0,0,0.45),0_0_18px_rgba(234,179,8,0.14)] xl:w-[76px] 2xl:w-[88px] data-[state=active]:z-10 data-[state=active]:border-yellow-500 data-[state=active]:bg-gradient-to-b data-[state=active]:from-yellow-300 data-[state=active]:via-yellow-500 data-[state=active]:to-yellow-700 data-[state=active]:text-black data-[state=active]:shadow-[inset_0_1px_0_rgba(255,255,255,0.55),inset_0_-3px_0_rgba(0,0,0,0.25),0_10px_24px_rgba(234,179,8,0.35)]";
  return (
    <div className="min-h-screen p-4">
      <div className="max-w-[1600px] mx-auto">
        <div className="bg-card border border-border rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              {brandingSettings.app_logo_url ? (
                <img
                  src={brandingSettings.app_logo_url}
                  alt={brandingSettings.app_name || "App Logo"}
                  className="h-14 max-w-[220px] object-contain"
                />
              ) : (
                <div>
                  <h1 className="text-3xl font-bold golden-text">
                    Admin Dashboard
                  </h1>
                  <p className="text-muted-foreground">Welcome, {adminName}</p>
                </div>
              )}

              {brandingSettings.app_logo_url && (
                <div>
                  <h1 className="text-3xl font-bold golden-text">
                    {brandingSettings.app_name || "Admin Dashboard"}
                  </h1>
                  <p className="text-muted-foreground">
                    {brandingSettings.app_tagline || `Welcome, ${adminName}`}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setShowEmailReport(!showEmailReport)}
                variant="outline"
                className="border-yellow-600 text-yellow-400 hover:bg-yellow-900"
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Report
              </Button>

              <Button
                onClick={onLogout}
                variant="outline"
                className="border-red-600 text-red-400 hover:bg-red-900"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {showEmailReport && (
          <div className="mb-6">
            <EmailReportForm
              reportData={{ drivers, adminSettings }}
              reportType="Admin Summary"
              onClose={() => setShowEmailReport(false)}
            />
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="overflow-hidden rounded-2xl border border-border bg-card/90 shadow-[0_22px_55px_rgba(0,0,0,0.45)]">
            <div className="border-b border-yellow-900/30 bg-gradient-to-b from-zinc-900 via-zinc-950 to-black px-3 pt-3">
              <div className="overflow-visible pb-0">
                <TabsList className="flex h-auto w-full flex-wrap items-end justify-start gap-1 overflow-visible rounded-none border-0 bg-transparent p-0 shadow-none">
                  {visibleMainTabs.map((item) => {
                    const Icon = item.icon;

                    return (
                      <TabsTrigger
                        key={item.value}
                        value={item.value}
                        title={item.label}
                        aria-label={item.label}
                        className={mainTabClass}
                      >
                        <span className="pointer-events-none absolute inset-x-2 top-1 h-px bg-white/20 group-data-[state=active]:bg-white/40" />
                        <span className="flex h-full flex-col items-center justify-center gap-1 leading-none">
                          <Icon className="h-5 w-5 shrink-0 opacity-90 group-data-[state=active]:opacity-100 xl:h-[18px] xl:w-[18px]" />
                          <span className="hidden max-w-full text-center text-[10px] font-bold leading-[1.05] tracking-tight xl:block">
                            {item.label}
                          </span>
                        </span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            </div>

            <div className="min-h-[520px] border-t border-yellow-500/20 bg-gradient-to-b from-zinc-950/30 via-background to-background p-4 md:p-6 shadow-[inset_0_1px_0_rgba(234,179,8,0.08)]">
              <TabsContent value="closedcheck" className="m-0">
                <ClosedCheck />
              </TabsContent>

              <TabsContent value="matchhub" className="m-0">
                <MatchHub />
              </TabsContent>

              <TabsContent value="summary" className="m-0">
                <WeeklyPayoutWorkspace />
              </TabsContent>

              <TabsContent value="timesheets" className="m-0">
                <TimesheetReport />
              </TabsContent>

              <TabsContent value="drivers" className="m-0">
                <DriverManagement />
              </TabsContent>

              <TabsContent value="fleet" className="m-0">
                <FleetManagement />
              </TabsContent>

              <TabsContent value="fleetpulse" className="m-0">
                <FleetPulse />
              </TabsContent>

              <TabsContent value="fleetcapacity" className="m-0">
                <FleetCapacityViewPage />
              </TabsContent>

              <TabsContent value="roster" className="m-0">
                <RosterPage />
              </TabsContent>

              <TabsContent value="jobinput" className="m-0">
                <JobInput />
              </TabsContent>

              <TabsContent value="reports" className="m-0">
                <ReportUpload />
              </TabsContent>

              <TabsContent value="crm" className="m-0">
                <CRMAccountsPage />
              </TabsContent>

              <TabsContent value="exportbuilder" className="m-0">
                <ExportBuilder />
              </TabsContent>

              <TabsContent value="ecard" className="m-0">
                <AdminECardHub />
              </TabsContent>

              <TabsContent value="payout" className="m-0">
                <PayoutHub />
              </TabsContent>

              <TabsContent value="insurance" className="m-0">
                <InsuranceHub />
              </TabsContent>

              <TabsContent value="ivrcalls" className="m-0">
                <IvrCallsHub />
              </TabsContent>

              <TabsContent value="commsbook" className="m-0">
                {/* A restricted Team Access user can be granted "view" on
                    Comms Book without "edit" -- this used to always render
                    the full admin composer (manage categories, post,
                    approve) regardless of their actual grant, which is what
                    let a view-only portal user reach a composer that then
                    failed to post. Falls back to the driver-style
                    suggest/read view, same as everyone without edit rights
                    gets on their own dashboard. */}
                <CommsBook currentDriverName={adminName} isDispatchOrAdmin={canEditTab(effectivePermissions, "commsbook")} />
              </TabsContent>

              <TabsContent value="settings" className="m-0">
                <div className="space-y-6">
                  <FolderTabLayout
                    tabs={settingsSubTabCatalog
                      .filter((t) => isModuleEnabled(t.moduleKey))
                      .map(({ moduleKey: _moduleKey, ...tab }) => tab)}
                    value={settingsSubTab}
                    onValueChange={setSettingsSubTab}
                  >
                  {settingsSubTab === "sitemap" && <AdminSiteMap />}

                  {settingsSubTab === "driver-notices" && <DriverNoticeBoardSettings />}

                  {settingsSubTab === "taxicaller-companies" && <TaxiCallerCompaniesSettings />}

                  {settingsSubTab === "team-access" && <TeamAccessSettings />}

                  {settingsSubTab === "company" && <CompanySettings />}

                  {settingsSubTab === "branding" && <CompanyBrandingSettings />}

                  {settingsSubTab === "payment-types" && (
                    <PaymentTypeManagement />
                  )}


                  {settingsSubTab === "payment-builder" && (
                    <DriverPaymentBuilder />
                  )}

                  {settingsSubTab === "driver-types" && (
                    <DriverTypeManagement />
                  )}

                  {settingsSubTab === "payment-settings" && (
                    <div className="space-y-6">
                    <Card className="shadow-lg bg-card border border-border">
                      <CardHeader>
                        <CardTitle className="text-xl text-primary">
                          Payment Settings
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          GST Percentage moved to Settings → Company → Company Info, alongside the new GST Number field.
                        </p>
                      </CardHeader>

                      <CardContent className="space-y-4">
                        <div>
                          <Label className="text-foreground">
                            Insurance Excess ($)
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={tempInsuranceExcess}
                            onChange={(e) =>
                              setTempInsuranceExcess(e.target.value)
                            }
                            className="dark-input"
                          />
                        </div>

                        <div>
                          <Label className="text-foreground">
                            Commission Percentage (%)
                          </Label>
                          <Input
                            type="number"
                            step="0.1"
                            value={tempCommissionPercent}
                            onChange={(e) =>
                              setTempCommissionPercent(e.target.value)
                            }
                            className="dark-input"
                          />
                        </div>

                        <div>
                          <Label className="text-foreground">
                            TM Hoist Fee ($)
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={tempHoistFee}
                            onChange={(e) => setTempHoistFee(e.target.value)}
                            className="dark-input"
                          />
                        </div>

                        <Button
                          onClick={updateSettings}
                          className="w-full golden-button"
                        >
                          Update Payment Settings
                        </Button>
                      </CardContent>
                    </Card>
                    <DeductionCategoriesManager />
                    <PaymentCategoriesManager />
                    </div>
                  )}

                  {settingsSubTab === "ecard-defaults" && (
                    <Card className="shadow-lg bg-card border border-border">
                      <CardHeader>
                        <CardTitle className="text-xl text-primary">
                          eCard Promo Defaults
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="space-y-6">
                        <div className="flex justify-end">
                          <Button
                            variant="outline"
                            onClick={loadECardDefaultsSettings}
                            disabled={eCardDefaultsLoading}
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {eCardDefaultsLoading ? "Refreshing..." : "Refresh"}
                          </Button>
                        </div>

                        <div className="rounded-lg border border-border bg-muted/20 p-4">
                          <p className="text-sm text-muted-foreground">
                            These settings control the default values used when
                            a new eCard promotion is created. The actual promo
                            can still be edited in the eCard promotions area.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {renderECardNumberSetting(
                            "ecard_promo_min_qualifying_amount",
                            "Minimum Qualifying Amount ($)",
                            "Minimum top-up amount required before the default promo bonus applies.",
                            "0.01",
                          )}

                          {renderECardNumberSetting(
                            "ecard_promo_below_min_bonus_rate",
                            "Below-Min Bonus Rate",
                            "Optional bonus rate for top-ups below the minimum. 0.50 means 50%.",
                            "0.01",
                          )}

                          {renderECardNumberSetting(
                            "ecard_promo_max_bonus_base",
                            "Maximum Bonus Base ($)",
                            "Maximum top-up amount used when calculating the default bonus.",
                            "0.01",
                          )}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
                            <div>
                              <Label className="text-foreground">
                                Default Bonus Type
                              </Label>
                              <p className="text-xs text-muted-foreground mt-1">
                                Choose the default calculation type for new
                                promos.
                              </p>
                            </div>

                            <select
                              value={
                                eCardDefaultsSettings.ecard_promo_bonus_type
                              }
                              onChange={(e) =>
                                updateECardDefaultField(
                                  "ecard_promo_bonus_type",
                                  e.target.value,
                                )
                              }
                              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                            >
                              <option value="PERCENT">Percentage bonus</option>
                              <option value="PER_BASE_AMOUNT">
                                Bonus per base amount
                              </option>
                              <option value="FIXED_AMOUNT">
                                Fixed bonus amount
                              </option>
                            </select>
                          </div>

                          <div className="space-y-3 rounded-lg border border-border bg-background/40 p-4">
                            <div>
                              <Label className="text-foreground">
                                Default Promo Tag Colour
                              </Label>
                              <p className="text-xs text-muted-foreground mt-1">
                                Default colour used when creating a new promo
                                tag.
                              </p>
                            </div>

                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={
                                  eCardDefaultsSettings.ecard_promo_default_tag_color ||
                                  "#3b82f6"
                                }
                                onChange={(e) =>
                                  updateECardDefaultField(
                                    "ecard_promo_default_tag_color",
                                    e.target.value,
                                  )
                                }
                                className="h-10 w-16 p-1"
                              />

                              <Input
                                value={
                                  eCardDefaultsSettings.ecard_promo_default_tag_color
                                }
                                onChange={(e) =>
                                  updateECardDefaultField(
                                    "ecard_promo_default_tag_color",
                                    e.target.value,
                                  )
                                }
                                placeholder="#3b82f6"
                                className="dark-input"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          {renderECardNumberSetting(
                            "ecard_promo_percent_rate",
                            "Default Percent Rate",
                            "Used for percentage promos. 1.00 means 100%, 0.50 means 50%.",
                            "0.01",
                          )}

                          {renderECardNumberSetting(
                            "ecard_promo_base_amount",
                            "Default Base Amount ($)",
                            "Used for per-base promos. Example: every $25 gets a $5 bonus.",
                            "0.01",
                          )}

                          {renderECardNumberSetting(
                            "ecard_promo_bonus_per_base",
                            "Default Bonus Per Base ($)",
                            "Bonus amount given for each base amount reached.",
                            "0.01",
                          )}

                          {renderECardNumberSetting(
                            "ecard_promo_fixed_bonus_amount",
                            "Default Fixed Bonus Amount ($)",
                            "Used for fixed bonus promos. Example: $10 flat bonus.",
                            "0.01",
                          )}
                        </div>

                        <Button
                          onClick={saveECardDefaultsSettings}
                          disabled={eCardDefaultsSaving}
                          className="w-full golden-button"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {eCardDefaultsSaving
                            ? "Saving eCard Defaults..."
                            : "Save eCard Defaults"}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {settingsSubTab === "fleet-defaults" && (
                    <Card className="shadow-lg bg-card border border-border overflow-hidden">
                      <CardHeader className="border-b border-border bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <CardTitle className="text-xl text-primary flex items-center gap-2">
                              <Car className="w-5 h-5" />
                              Fleet / Compliance Defaults
                            </CardTitle>
                            <p className="text-sm text-muted-foreground mt-1">
                              Configure fleet defaults, warning windows and
                              traffic-light thresholds.
                            </p>
                          </div>

                          <Button
                            variant="outline"
                            onClick={loadFleetDefaultsSettings}
                            disabled={fleetDefaultsLoading}
                            className="self-start md:self-auto"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {fleetDefaultsLoading ? "Refreshing..." : "Refresh"}
                          </Button>
                        </div>
                      </CardHeader>

                      <CardContent className="p-0 bg-zinc-950">
                        <SettingsSidebarLayout
                          tabs={[
                            { value: "fleet-setup", label: "Fleet Setup", description: "Vehicle defaults", icon: Car },
                            { value: "fleet-checks", label: "Fleet Checks", description: "Check interval & lights", icon: Gauge },
                            { value: "cof", label: "COF", description: "Certificate warnings", icon: ShieldCheck },
                            { value: "rego", label: "Rego", description: "Registration warnings", icon: CalendarClock },
                            { value: "maintenance", label: "Maintenance", description: "Service days & km", icon: Wrench },
                            { value: "ruc", label: "RUC", description: "Road user charges", icon: Fuel },
                          ]}
                          value={fleetDefaultsSubTab}
                          onValueChange={setFleetDefaultsSubTab}
                          minHeight="min-h-[460px]"
                        >
                            {fleetDefaultsSubTab === "fleet-setup" && (
                              <div className="space-y-5">
                                <div>
                                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                    <Car className="h-5 w-5 text-yellow-500" />
                                    Fleet Setup
                                  </h3>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Defaults used when creating or importing
                                    vehicles.
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                  {renderFleetTextSetting(
                                    "fleet_default_vehicle_type",
                                    "Default Vehicle Type",
                                    "Default vehicle type when creating a new vehicle. Manage the list itself in Settings → Vehicle Types.",
                                    vehicleTypeOptions.map((t) => t.vehicle_type),
                                  )}

                                  {renderFleetTextSetting(
                                    "fleet_default_status",
                                    "Default Vehicle Status",
                                    "Default status applied to new vehicles.",
                                    [
                                      "Active",
                                      "Inactive",
                                      "Maintenance",
                                      "Retired",
                                    ],
                                  )}

                                  {renderFleetTextSetting(
                                    "fleet_default_fuel_type",
                                    "Default Fuel Type",
                                    "Default fuel type for new vehicles.",
                                    [
                                      "Petrol",
                                      "Diesel",
                                      "Hybrid",
                                      "Electric",
                                      "Other",
                                    ],
                                  )}

                                  {renderFleetNumberSetting(
                                    "fleet_vehicle_inactive_after_days",
                                    "Inactive After Days",
                                    "Flag a vehicle as inactive if it has not been updated or used within this many days.",
                                  )}

                                  {renderFleetNumberSetting(
                                    "fleet_vehicle_inspection_warning_days",
                                    "Inspection Warning Days",
                                    "Warn when a vehicle inspection is due within this many days.",
                                  )}
                                </div>
                              </div>
                            )}

                            {fleetDefaultsSubTab === "fleet-checks" && (
                              <div className="space-y-5">
                                <div>
                                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                    <Gauge className="h-5 w-5 text-yellow-500" />
                                    Fleet Check Settings
                                  </h3>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Control how often vehicle checks are due and when they change colour.
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                  {renderFleetNumberSetting(
                                    "fleet_vehicle_check_interval_days",
                                    "Fleet Check Interval Days",
                                    "A check becomes due once this many days have passed since the vehicle's last check. Example: 1 means a check is due every day.",
                                  )}

                                  {renderFleetNumberSetting(
                                    "fleet_vehicle_check_shift_threshold",
                                    "Fleet Check Shift Threshold",
                                    "A check also becomes due once this many completed shifts have happened since the last check, whichever comes first against the day interval.",
                                  )}

                                  {renderFleetNumberSetting(
                                    "fleet_vehicle_inspection_warning_days",
                                    "Inspection Warning Days",
                                    "Warn when a vehicle inspection is due within this many days.",
                                  )}
                                </div>

                                <div className="space-y-3">
                                  <div>
                                    <h4 className="text-base font-semibold text-foreground">Admin inspections &amp; camera checks</h4>
                                    <p className="text-sm text-muted-foreground mt-1">
                                      How often each vehicle is due an admin inspection and a camera check. Vehicles that are overdue or coming
                                      due show on Closed Check each week and in the Fleet list. A single vehicle can use its own interval: set
                                      it on that vehicle's Edit screen.
                                    </p>
                                  </div>
                                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                    {renderFleetNumberSetting(
                                      "admin_inspection_interval_months",
                                      "Admin Inspection Every (months)",
                                      "A vehicle is due an admin inspection this many months after its last one.",
                                    )}

                                    {renderFleetNumberSetting(
                                      "camera_check_interval_months",
                                      "Camera Check Every (months)",
                                      "A vehicle's camera is due a check this many months after its last one.",
                                    )}

                                    {renderFleetNumberSetting(
                                      "fleet_checks_due_soon_days",
                                      "Due Soon Window (days)",
                                      "Checks show as due soon, and appear on Closed Check, this many days before they fall due.",
                                    )}
                                  </div>
                                </div>

                                {renderFleetDayTrafficSettings(
                                  "fleet_inspection_green_min_days",
                                  "fleet_inspection_amber_warning_days",
                                  "fleet_inspection_red_critical_days",
                                  "Fleet Check Traffic Lights",
                                )}

                                {renderFleetSwitchSetting(
                                  "fleet_vehicle_check_require_odometer",
                                  "Require Odometer Reading",
                                  "Require the actual km reading (not just a photo) when a driver does a Vehicle Check. Updates the vehicle's last known KM automatically -- only when higher than what's on file -- feeding straight into RUC/service tracking.",
                                )}

                                {renderFleetTextSetting(
                                  "fleet_vehicle_check_lockout_message",
                                  "Fleet Check Lockout Message",
                                  "Shown to a driver once their vehicle check is overdue -- every tab except Vehicle Check locks until they complete one.",
                                  undefined,
                                  true,
                                )}

                                <VehicleCheckItemsSettings />
                              </div>
                            )}

                            {fleetDefaultsSubTab === "cof" && (
                              <div className="space-y-5">
                                <div>
                                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                    <ShieldCheck className="h-5 w-5 text-yellow-500" />
                                    COF Settings
                                  </h3>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Warning rules for Certificate of Fitness
                                    expiry.
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                  {renderFleetNumberSetting(
                                    "fleet_cof_warning_days",
                                    "COF Warning Days",
                                    "Warn when COF expiry is within this many days.",
                                  )}

                                  {renderFleetNumberSetting(
                                    "fleet_cof_default_interval_months",
                                    "Default Interval (months)",
                                    "Default COF/WOF renewal length, e.g. 6 for COF or 12 for WOF. Override per vehicle for anything on a different schedule.",
                                  )}

                                  {renderFleetNumberSetting(
                                    "fleet_cof_flex_months",
                                    "Flex Months",
                                    "How many months early an inspection can be booked to land in a lighter month. Never suggests booking late.",
                                  )}

                                  {renderFleetMonthsSetting(
                                    "fleet_cof_allowed_months",
                                    "Allowed Months",
                                    "Months COF/WOF is allowed to be booked in -- renewals get spread evenly across whichever of these are lightest.",
                                  )}
                                </div>

                                {renderFleetDayTrafficSettings(
                                  "fleet_cof_green_min_days",
                                  "fleet_cof_amber_warning_days",
                                  "fleet_cof_red_critical_days",
                                  "COF Traffic Lights",
                                )}
                              </div>
                            )}

                            {fleetDefaultsSubTab === "rego" && (
                              <div className="space-y-5">
                                <div>
                                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                    <CalendarClock className="h-5 w-5 text-yellow-500" />
                                    Registration Settings
                                  </h3>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Warning rules for registration expiry.
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                  {renderFleetNumberSetting(
                                    "fleet_rego_warning_days",
                                    "Registration Warning Days",
                                    "Warn when registration expiry is within this many days.",
                                  )}

                                  {renderFleetMonthsSetting(
                                    "fleet_rego_allowed_months",
                                    "Allowed Months",
                                    "Months registration is allowed to be renewed in -- e.g. exclude December/January to avoid a big outgoing over the holidays. Renewals get spread evenly across whichever of these are lightest.",
                                  )}
                                </div>

                                {renderFleetDayTrafficSettings(
                                  "fleet_rego_green_min_days",
                                  "fleet_rego_amber_warning_days",
                                  "fleet_rego_red_critical_days",
                                  "Rego Traffic Lights",
                                )}
                              </div>
                            )}

                            {fleetDefaultsSubTab === "maintenance" && (
                              <div className="space-y-5">
                                <div>
                                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                    <Wrench className="h-5 w-5 text-yellow-500" />
                                    Maintenance Settings
                                  </h3>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Service warnings based on days and
                                    kilometres.
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                  {renderFleetNumberSetting(
                                    "fleet_service_warning_days",
                                    "Service Warning Days",
                                    "Warn when vehicle service is due within this many days.",
                                  )}

                                  {renderFleetNumberSetting(
                                    "fleet_service_warning_km",
                                    "Service Warning KM",
                                    "Warn when vehicle is within this many kilometres of service due.",
                                  )}
                                </div>

                                {renderFleetDayTrafficSettings(
                                  "fleet_maintenance_green_min_days",
                                  "fleet_maintenance_amber_warning_days",
                                  "fleet_maintenance_red_critical_days",
                                  "Maintenance Date Traffic Lights",
                                )}

                                {renderFleetKmTrafficSettings(
                                  "fleet_maintenance_green_min_km",
                                  "fleet_maintenance_amber_warning_km",
                                  "fleet_maintenance_red_critical_km",
                                  "Maintenance KM Traffic Lights",
                                )}
                              </div>
                            )}

                            {fleetDefaultsSubTab === "ruc" && (
                              <div className="space-y-5">
                                <div>
                                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                    <Fuel className="h-5 w-5 text-yellow-500" />
                                    RUC Settings
                                  </h3>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Road User Charges warning thresholds.
                                  </p>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                  {renderFleetNumberSetting(
                                    "fleet_ruc_warning_km",
                                    "RUC Warning KM",
                                    "Warn when vehicle is within this many kilometres of RUC expiry.",
                                  )}
                                </div>

                                {renderFleetKmTrafficSettings(
                                  "fleet_ruc_green_min_km",
                                  "fleet_ruc_amber_warning_km",
                                  "fleet_ruc_red_critical_km",
                                  "RUC Traffic Lights",
                                )}
                              </div>
                            )}

                            {fleetDefaultsSubTab === "traffic-lights" && (
                              <div className="space-y-5">
                                <div>
                                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                    <Gauge className="h-5 w-5 text-yellow-500" />
                                    Traffic Light Thresholds
                                  </h3>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    Control when compliance items show as green,
                                    amber, or red.
                                  </p>
                                </div>

                                <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-4">
                                  <div>
                                    <h4 className="font-medium text-foreground">
                                      Days Remaining
                                    </h4>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Used for COF, registration, inspection and
                                      other date-based compliance.
                                    </p>
                                  </div>

                                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                    {renderFleetNumberSetting(
                                      "fleet_green_min_days",
                                      "Green Minimum Days",
                                      "More than this many days remaining is green.",
                                    )}

                                    {renderFleetNumberSetting(
                                      "fleet_amber_warning_days",
                                      "Amber Warning Days",
                                      "This many days or fewer remaining is amber.",
                                    )}

                                    {renderFleetNumberSetting(
                                      "fleet_red_critical_days",
                                      "Red Critical Days",
                                      "This many days or fewer remaining is red.",
                                    )}
                                  </div>
                                </div>

                                <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-4">
                                  <div>
                                    <h4 className="font-medium text-foreground">
                                      Kilometres Remaining
                                    </h4>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Used for RUC and service-based kilometre
                                      warnings.
                                    </p>
                                  </div>

                                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                    {renderFleetNumberSetting(
                                      "fleet_green_min_km",
                                      "Green Minimum KM",
                                      "More than this many kilometres remaining is green.",
                                    )}

                                    {renderFleetNumberSetting(
                                      "fleet_amber_warning_km",
                                      "Amber Warning KM",
                                      "This many kilometres or fewer remaining is amber.",
                                    )}

                                    {renderFleetNumberSetting(
                                      "fleet_red_critical_km",
                                      "Red Critical KM",
                                      "This many kilometres or fewer remaining is red.",
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                        </SettingsSidebarLayout>

                        <div className="border-t border-border bg-black/25 p-4">
                          <Button
                            onClick={saveFleetDefaultsSettings}
                            disabled={fleetDefaultsSaving}
                            className="w-full golden-button"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            {fleetDefaultsSaving
                              ? "Saving Fleet Defaults..."
                              : "Save Fleet Defaults"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {settingsSubTab === "insurance-settings" && (
                    <Card className="shadow-lg bg-card border border-border overflow-hidden">
                      <CardHeader className="border-b border-border bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950">
                        <CardTitle className="text-xl text-primary flex items-center gap-2">
                          <ShieldAlert className="w-5 h-5" />
                          Insurance Settings
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          Broker details for claim exports, excess fund tiers, and which fields the driver-portal
                          incident report requires.
                        </p>
                      </CardHeader>

                      <CardContent className="p-0 bg-zinc-950">
                        <SettingsSidebarLayout
                          tabs={[
                            { value: "broker", label: "Broker Details", description: "Rothbury Make a Claim fields", icon: Landmark },
                            { value: "excess", label: "Excess Fund", description: "Graduated excess tiers & contributions", icon: ShieldAlert },
                            { value: "incident-fields", label: "Incident Report Fields", description: "Driver-portal report requirements", icon: FileWarning },
                          ]}
                          value={insuranceSettingsSubTab}
                          onValueChange={setInsuranceSettingsSubTab}
                          minHeight="min-h-[460px]"
                        >
                          {insuranceSettingsSubTab === "broker" && <InsuranceBrokerSettings />}
                          {insuranceSettingsSubTab === "excess" && <InsuranceExcessSettings />}
                          {insuranceSettingsSubTab === "incident-fields" && <IncidentReportFieldSettings />}
                        </SettingsSidebarLayout>
                      </CardContent>
                    </Card>
                  )}

                  {settingsSubTab === "bonus-tiers" && <BonusTierManagement />}

                  {settingsSubTab === "roster-positions" && (
                    <RosterPositionsManagement />
                  )}

                  {settingsSubTab === "vehicle-capacity" && (
                    <VehicleTypeCapacitySettings />
                  )}

                  {settingsSubTab === "roster-groups" && (
                    <RosterGroupsManagement />
                  )}

                  {settingsSubTab === "roster-holidays" && (
                    <RosterHolidayManagement />
                  )}

                  {settingsSubTab === "work-time-rules" && (
                    <WorkTimeRulesSettings />
                  )}

                  {settingsSubTab === "maintenance-lists" && (
                    <MaintenanceSettings />
                  )}

                  {settingsSubTab === "ivr" && <IvrGeneralSettings />}
                  </FolderTabLayout>
                </div>
              </TabsContent>
            </div>
          </div>
        </Tabs>
      </div>

      {/*
        Admin Issues popup disabled.
        Alerts now handled inside Closed Check → Action Required tabs.
        To restore the old popup later, uncomment the import near the top
        and uncomment the AdminIssuesDialog component below.
      */}
      {/* <AdminIssuesDialog /> */}
    </div>
  );
};

export default AdminDashboard;
